export interface ParsedExifData {
  latitude?: number;
  longitude?: number;
  altitude?: number;
  takenAt?: string;
  make?: string;
  model?: string;
  lensModel?: string;
  orientation?: number;
  imageWidth?: number;
  imageHeight?: number;
  rawExif?: Record<string, unknown>;
}

/** Postgres SQLSTATE for "unsupported Unicode escape sequence" (e.g. U+0000 in a jsonb value). */
export const PG_UNSUPPORTED_UNICODE_ESCAPE = '22P05';

/** Raw EXIF strings longer than this (MakerNote and similar blobs) are not stored. */
export const MAX_RAW_EXIF_STRING_LENGTH = 2000;
/** Raw EXIF arrays longer than this (byte dumps) are not stored. */
export const MAX_RAW_EXIF_ARRAY_LENGTH = 256;
const MAX_RAW_EXIF_DEPTH = 8;

const NUL_CHARS = new RegExp(String.fromCharCode(0), 'g');

/** Removes NUL (U+0000) characters, which Postgres text and jsonb reject. */
export function stripNulChars(value: string): string {
  return value.replace(NUL_CHARS, '');
}

const DROP = Symbol('drop');

function isBinary(value: object): boolean {
  return ArrayBuffer.isView(value) || Object.prototype.toString.call(value).endsWith('ArrayBuffer]');
}

function sanitizeExifValue(value: unknown, depth: number, ancestors: Set<object>): unknown {
  if (value === null) return null;
  switch (typeof value) {
    case 'string': {
      const clean = stripNulChars(value);
      return clean.length > MAX_RAW_EXIF_STRING_LENGTH ? DROP : clean;
    }
    case 'number':
      return Number.isFinite(value) ? value : DROP;
    case 'boolean':
      return value;
    case 'object':
      break;
    default:
      // undefined, function, symbol, bigint
      return DROP;
  }

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? DROP : value.toISOString();
  }
  if (isBinary(value) || depth >= MAX_RAW_EXIF_DEPTH || ancestors.has(value)) return DROP;

  ancestors.add(value);
  try {
    if (Array.isArray(value)) {
      if (value.length > MAX_RAW_EXIF_ARRAY_LENGTH) return DROP;
      // Keep positions stable (e.g. GPS [deg, min, sec]); unstorable items become null, as JSON.stringify does.
      return value.map((item) => {
        const clean = sanitizeExifValue(item, depth + 1, ancestors);
        return clean === DROP ? null : clean;
      });
    }
    const out: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value)) {
      const cleanKey = stripNulChars(key);
      if (!cleanKey) continue;
      const clean = sanitizeExifValue(item, depth + 1, ancestors);
      if (clean !== DROP) out[cleanKey] = clean;
    }
    return out;
  } finally {
    ancestors.delete(value);
  }
}

/**
 * Returns a copy of a raw EXIF dictionary that can be stored in a Postgres jsonb column:
 * NUL characters removed from keys and strings, binary/function/undefined/non-finite values
 * dropped, Dates converted to ISO strings, oversized strings and arrays dropped, and cycles
 * and very deep nesting cut off.
 */
export function toJsonSafeExif(raw: Record<string, unknown>): Record<string, unknown> {
  const clean = sanitizeExifValue(raw, 0, new Set());
  return clean !== null && typeof clean === 'object' && !Array.isArray(clean)
    ? (clean as Record<string, unknown>)
    : {};
}

/** The normalised EXIF fields without the raw dictionary, for when the raw data cannot be stored. */
export function withoutRawExif(parsed: ParsedExifData): ParsedExifData {
  const fields = { ...parsed };
  delete fields.rawExif;
  return fields;
}

/** True when a Supabase/Postgres error is SQLSTATE 22P05 "unsupported Unicode escape sequence". */
export function isUnsupportedUnicodeEscapeError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    error.code === PG_UNSUPPORTED_UNICODE_ESCAPE
  );
}

function cleanExifText(value: unknown): string | undefined {
  return value ? stripNulChars(String(value)).trim() : undefined;
}

export function dmsToDecimal(dms: number[], ref: string): number | undefined {
  if (!Array.isArray(dms) || dms.length < 3) return undefined;
  const [d, m, s] = dms;
  if (!Number.isFinite(d) || !Number.isFinite(m) || !Number.isFinite(s)) return undefined;
  let dec = d + m / 60 + s / 3600;
  if (ref === 'S' || ref === 'W') dec = -dec;
  return dec;
}

/**
 * Normalizes raw EXIF metadata into a standard ParsedExifData object.
 * rawExif is sanitised with toJsonSafeExif so the result can be stored in a jsonb column.
 */
export function normalizeExifObj(raw: Record<string, unknown>): ParsedExifData {
  let latitude: number | undefined;
  let longitude: number | undefined;

  const rawLat = raw.latitude ?? raw.GPSLatitude;
  const rawLng = raw.longitude ?? raw.GPSLongitude;
  const latRef = String(raw.GPSLatitudeRef || 'N');
  const lngRef = String(raw.GPSLongitudeRef || 'E');

  if (typeof raw.latitude === 'number' && Number.isFinite(raw.latitude)) {
    latitude = raw.latitude;
  } else if (typeof raw.GPSLatitude === 'number' && Number.isFinite(raw.GPSLatitude)) {
    latitude = raw.GPSLatitude;
  } else if (Array.isArray(rawLat)) {
    latitude = dmsToDecimal(rawLat as number[], latRef);
  }

  if (typeof raw.longitude === 'number' && Number.isFinite(raw.longitude)) {
    longitude = raw.longitude;
  } else if (typeof raw.GPSLongitude === 'number' && Number.isFinite(raw.GPSLongitude)) {
    longitude = raw.GPSLongitude;
  } else if (Array.isArray(rawLng)) {
    longitude = dmsToDecimal(rawLng as number[], lngRef);
  }

  if (typeof latitude === 'number') {
    if (latRef === 'S' && latitude > 0) latitude = -latitude;
  }
  if (typeof longitude === 'number') {
    if (lngRef === 'W' && longitude > 0) longitude = -longitude;
  }

  const rawDate = raw.DateTimeOriginal || raw.CreateDate || raw.ModifyDate;
  let takenAt: string | undefined;
  if (rawDate instanceof Date && !isNaN(rawDate.getTime())) {
    takenAt = rawDate.toISOString();
  } else if (typeof rawDate === 'string' || typeof rawDate === 'number') {
    const d = new Date(rawDate);
    if (!isNaN(d.getTime())) takenAt = d.toISOString();
  }

  return {
    latitude: typeof latitude === 'number' && Number.isFinite(latitude) ? latitude : undefined,
    longitude: typeof longitude === 'number' && Number.isFinite(longitude) ? longitude : undefined,
    altitude: typeof raw.GPSAltitude === 'number' && Number.isFinite(raw.GPSAltitude) ? raw.GPSAltitude : undefined,
    takenAt,
    make: cleanExifText(raw.Make),
    model: cleanExifText(raw.Model),
    lensModel: cleanExifText(raw.LensModel),
    orientation: typeof raw.Orientation === 'number' ? raw.Orientation : (typeof raw.orientation === 'number' ? raw.orientation : undefined),
    imageWidth: (raw.ExifImageWidth || raw.ImageWidth) as number | undefined,
    imageHeight: (raw.ExifImageHeight || raw.ImageHeight) as number | undefined,
    rawExif: toJsonSafeExif(raw),
  };
}
